// src/components/miniPortfolio/MiniPortfolioSummary.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Grid,
  Alert
} from '@mui/material';
import {
  Plus,
  Briefcase,
  TrendingUp,
  TrendingDown,
  LineChart
} from 'lucide-react';
import axios from '../../config/axiosConfig';

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercentage = (percentage) => {
  if (percentage === null || percentage === undefined || isNaN(percentage)) return '0.00%';
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
};

const getValueColor = (value) => {
  if (value > 0) return '#0eb35b';
  if (value < 0) return '#e62333';
  return '#757575';
};

const MiniPortfolioSummary = ({
  userId,
  className = ''
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPortfolioIndex, setSelectedPortfolioIndex] = useState(null);
  const [, setRefreshing] = useState(false);

  const fetchMiniPortfolioData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/portfolios/mini/${userId}`);
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to load portfolio data');
      }
    } catch (err) {
      console.error('Error fetching mini portfolio data:', err);
      setError(err.response?.data?.message || 'Error loading portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMiniPortfolioData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (userId) {
      fetchMiniPortfolioData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (userId && data?.state === 'ACTIVE_PORTFOLIOS') {
      const interval = setInterval(fetchMiniPortfolioData, 60000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, data?.state]);

  const handleTabChange = (event, newValue) => {
    setSelectedPortfolioIndex(newValue === 0 ? null : newValue - 1);
  };

  const getCurrentPortfolioData = () => {
    if (!data || data.state !== 'ACTIVE_PORTFOLIOS') return null;
    if (selectedPortfolioIndex === null) {
      return {
        name: 'All Portfolios',
        isAggregated: true,
        ...data.aggregatedData
      };
    }
    const portfolioDetail = data.portfolioDetails[selectedPortfolioIndex];
    return {
      name: portfolioDetail?.name || `Portfolio ${selectedPortfolioIndex + 1}`,
      isAggregated: false,
      ...portfolioDetail
    };
  };

  const getTabOptions = () => {
    if (!data || !data.portfolios) return [];
    const tabs = [{ label: 'All', index: null }];
    data.portfolios.forEach((portfolio, index) => {
      tabs.push({
        label: portfolio.name || `Portfolio ${index + 1}`,
        index: index
      });
    });
    return tabs;
  };

  // Render loading state
  if (loading) {
    return (
      <Card className={className} sx={{ height: '100%', minHeight: 200, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error) {
    return (
      <Card className={className} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Box textAlign="center">
            <Button onClick={handleRefresh} startIcon={<Plus size={16} />}>Retry</Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render no portfolios state
  if (!data || data.state === 'NO_PORTFOLIOS') {
    return (
      <Card className={className} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Box textAlign="center" py={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <Briefcase size={22} color="#3b82f6" strokeWidth={1.75} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
              Portfolio Performance
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>
              Create your first portfolio to start tracking
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={14} strokeWidth={2.5} />}
              onClick={() => navigate('/portfolio')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
            >
              Create Portfolio
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render empty portfolios state
  if (data.state === 'EMPTY_PORTFOLIOS') {
    const tabOptions = getTabOptions();
    const hasMultiplePortfolios = data.portfolios.length > 1;
    const currentPortfolioName = selectedPortfolioIndex === null
      ? 'All Portfolios'
      : data.portfolios[selectedPortfolioIndex]?.name || `Portfolio ${selectedPortfolioIndex + 1}`;

    return (
      <Card className={className} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {hasMultiplePortfolios && (
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
              <Tabs
                value={selectedPortfolioIndex === null ? 0 : selectedPortfolioIndex + 1}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 32 }}
              >
                {tabOptions.map((tab, index) => (
                  <Tab key={index} label={tab.label} sx={{ minHeight: 32, fontSize: '0.75rem', textTransform: 'none', padding: '6px 8px' }} />
                ))}
              </Tabs>
            </Box>
          )}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <LineChart size={22} color="#3b82f6" strokeWidth={1.75} />
            </Box>
            <Typography variant="body1" gutterBottom>
              {currentPortfolioName} {selectedPortfolioIndex === null && data.portfolios.length > 1 ? `(${data.portfolios.length} portfolios)` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add assets to start tracking performance
            </Typography>
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => navigate('/portfolio')} size="small">
              Add Your First Asset
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render active portfolios state
  const currentData = getCurrentPortfolioData();
  const tabOptions = getTabOptions();
  const hasMultiplePortfolios = data.portfolios.length > 1;

  if (!currentData) {
    return (
      <Card className={className} sx={{ height: '100%' }}>
        <CardContent>
          <Alert severity="warning">No portfolio data available</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
            Portfolio Performance
          </Typography>
          {hasMultiplePortfolios && (
            <Tabs
              value={selectedPortfolioIndex === null ? 0 : selectedPortfolioIndex + 1}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: { xs: 28, sm: 32 },
                '& .MuiTab-root': {
                  minHeight: { xs: 28, sm: 32 },
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  textTransform: 'none',
                  padding: { xs: '4px 6px', sm: '6px 8px' },
                  minWidth: { xs: 'auto', sm: 'auto' }
                }
              }}
            >
              {tabOptions.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
          )}
        </Box>

        {/* Main metrics grid */}
        <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center" justifyContent="center">
          <Grid item xs={6} sm={2.4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography sx={{ color: getValueColor(currentData.totalReturnPercentage), fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.2 }}>
                {formatPercentage(currentData.totalReturnPercentage)}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'text.secondary' }}>
                Total Return
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2.4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.2 }}>
                {formatCurrency(currentData.totalValue)}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'text.secondary' }}>
                Portfolio Value
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2.4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography sx={{ color: getValueColor(currentData.totalReturn), fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.2 }}>
                {formatCurrency(currentData.totalReturn)}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'text.secondary' }}>
                Total P&L
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2.4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography sx={{ color: getValueColor(currentData.dailyChange), fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.2 }}>
                {formatCurrency(currentData.dailyChange)}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'text.secondary' }}>
                Today's Change
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.2 }}>
                {currentData.holdingsCount}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.7rem' }, color: 'text.secondary' }}>
                Holdings
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom section */}
        <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
          >
            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, color: 'text.secondary', textAlign: { xs: 'center', sm: 'left' } }}>
              {currentData.name}
              {currentData.isAggregated && data.portfolios.length > 1 && ` • ${data.portfolios.length} portfolios`}
              {!currentData.isAggregated && currentData.holdingsCount === 0 && (
                <span style={{ color: '#ff9800', marginLeft: '8px' }}>• Empty Portfolio</span>
              )}
            </Typography>
            <Box display="flex" gap={1} width={{ xs: '100%', sm: 'auto' }}>
              <Button
                size="small"
                endIcon={<TrendingUp size={14} />}
                onClick={() => navigate('/portfolio')}
                variant="text"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, padding: { xs: '4px 8px', sm: '6px 12px' }, flex: { xs: 1, sm: 'none' } }}
              >
                Portfolios
              </Button>
            </Box>
          </Box>

          {(currentData.holdingsCount > 1 && (currentData.bestPerformer || currentData.worstPerformer)) && (
            <Box mt={1} display="flex" justifyContent="space-between" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.5, sm: 0 }}>
              {currentData.bestPerformer && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <TrendingUp size={13} color="#0eb35b" strokeWidth={2.25} />
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: 'success.main' }}>
                    Best: {currentData.bestPerformer.symbol} ({formatPercentage(currentData.bestPerformer.pnlPercentage)})
                  </Typography>
                </Box>
              )}
              {currentData.worstPerformer && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                  <TrendingDown size={13} color="#e62333" strokeWidth={2.25} />
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: 'error.main' }}>
                    Worst: {currentData.worstPerformer.symbol} ({formatPercentage(currentData.worstPerformer.pnlPercentage)})
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MiniPortfolioSummary;